// External imports
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import Cookies from "js-cookie";
import { Card, Col } from "react-bootstrap";
import { Fragment } from "react";
import moment from "moment";

// Internal imports
import { ILastVisits } from "types/Recent";
import Button from "components/atoms/Button/Button";
import testIds from "constants/testIds";

interface RecentlyVisitedWidgetProps {
  isLayoutLocked: boolean;
  isPreview?: boolean;
  widgetId: number;
  handleWidgetDialogOpen: (widgetId: number) => void;
}

function RecentlyVisitedWidget({ isLayoutLocked, isPreview, widgetId, handleWidgetDialogOpen }: RecentlyVisitedWidgetProps) {
  // Hooks
  const navigate = useNavigate();

  // Data declarations
  const visitHistory = Cookies.get("lastVisits") && JSON.parse(Cookies.get("lastVisits") || "");

  /**
   * Handle link click
   * @param display_type - quick request, quote_request, shipment, invoice
   * @param reference_number - shipment id or quick request id
   */
  const onLinkClick = (display_type: string, reference_number: string) => {
    if (isLayoutLocked && !isPreview) {
      navigate(generateUrl(display_type, reference_number));
    }
  };

  /**
   * Generates a url based on the type and reference
   * @param type - quick request, quote_request, shipment, invoice
   * @param reference - shipment id or quick request id
   * @returns a url string
   */
  function generateUrl(type: string, reference: string) {
    let path = "";

    switch (type) {
      case "Quick Request":
        path = `/shipment/${reference}/quick`;
        break;
      default:
        path = `/shipment/${reference}`;
    }
    return path;
  }

  return (
    <Card.Body className="overflow-auto">
      <div
        className={`d-flex justify-content-${
          isLayoutLocked ? "center" : "between"
        } align-items-center pb-2 br-5 bg-white rounded-bottom-left-0 rounded-bottom-right-0`}
      >
        {/* Title */}
        <h5 className="d-flex align-items-center">
          <FormattedMessage
            id="dashboard.widgetDialog.listWidgetBuilder.recentlyVisited"
            defaultMessage="Recently visited"
          />
        </h5>
        {/* Edit button */}
        {!isLayoutLocked && !isPreview && (
          <Button
            className="edit-btn"
            onClick={() => {
              handleWidgetDialogOpen(widgetId);
            }}
            data-testid={`${testIds.EDIT_WIDGET_BUTTON}-${widgetId}`}
          >
            <FormattedMessage id="general.edit" defaultMessage="Edit" />
          </Button>
        )}
      </div>
      {/* No history message */}
      {visitHistory?.length === 0 && (
        <div className="d-flex justify-content-center align-items-center" style={{ height: "80%" }}>
          <span className="text-secondary text-center">
            <FormattedMessage id="dashboard.widgetDialog.listWidgetBuilder.noHistory" defaultMessage="No history" />
          </span>
        </div>
      )}
      {/* History list */}
      <ul className="row flex-column list-widget-container">
        {visitHistory?.length > 0 &&
          visitHistory
            .slice(0, isPreview ? 2 : 10) // Only show 2 visits in preview mode
            .reverse() // Most recent visit first
            .map(({ date, reference_number, display_type, title }: ILastVisits, index: number) => (
              <Fragment key={reference_number}>
                <li
                  className={`col list-widget-item ${!isLayoutLocked || isPreview ? "previewing" : ""} ${
                    index === visitHistory.length - 1 ? "" : "border-bottom border-grey"
                  }`}
                >
                  <div
                    aria-hidden
                    onClick={() => onLinkClick(display_type, reference_number)}
                    className="d-flex row text-primary text-decoration-none pt-4 pb-4"
                  >
                    <Col className="d-flex flex-column align-items-start justify-content-start">
                      <p className="text-primary font-b4 d-block pb-1">{reference_number}</p>
                      <p className="font-b3">{title}</p>
                      <p className="font-b8 text-secondary pr-15 mt-1">{moment(date).fromNow()}</p>
                    </Col>
                  </div>
                </li>
                <hr className="list-widget-line" />
              </Fragment>
            ))}
      </ul>
    </Card.Body>
  );
}

RecentlyVisitedWidget.propTypes = {
  isLayoutLocked: PropTypes.bool.isRequired,
  isPreview: PropTypes.bool,
  widgetId: PropTypes.number,
  handleWidgetDialogOpen: PropTypes.func,
};

RecentlyVisitedWidget.defaultProps = {
  isPreview: false,
  widgetId: null,
  handleWidgetDialogOpen: () => {},
};

export default RecentlyVisitedWidget;
